/*
 * Winely → Klaviyo custom events bridge (CR #21)
 *
 * Routes DOM CustomEvents to Klaviyo via our backend (server-side Events API),
 * with a best-effort client SDK fallback for on-site flows. Bridges
 * email<->qid linkage so Klaviyo profiles stay merged across re-quizzes.
 */
(function () {
  'use strict';

  var IDENTIFY_KEY = 'winely_klaviyo_identified';
  var LINKED_KEY = 'winely_linked_qids';
  var BUFFER_KEY = 'winely_event_buffer';
  var BUFFER_MAX = 200; // hard cap so a runaway page can't blow up LS
  var LANDING_FLAG = !!new URLSearchParams(window.location.search).get('landing');
  var BACKEND_BASE = 'https://winely-backend.vercel.app';
  // No-op logger — kept so callers don't have to be edited if we want to
  // switch debug logging back on (just point log to console.log).
  function log() {}

  // The quiz controller posts to /api/quiz/link-email with the user-entered
  // email, but it never stores that email in localStorage, so our normal
  // readEmailFromLS() path can't see it. Without the email visible to this
  // script, ktrack() can't promote the buffer through linkEmailToQid().
  //
  // We solve it with a fetch interceptor: when we see a POST to link-email
  // from the quiz controller, we mirror the email into LS and fire OUR own
  // link-email request with the buffered events[] attached. Backend persists
  // them to quiz_event_buffer even on alreadySet=true; the flush cron picks
  // them up once Klaviyo flips the profile to SUBSCRIBED.
  //
  // Recursion guard: our own linkEmailToQid call also goes through fetch
  // (since we wrap window.fetch), so without a marker we'd intercept our
  // own request and loop forever — exact failure mode the previous deploy
  // hit, twice. We use a QUERY-STRING marker (not a custom header) on
  // purpose: a custom header triggers a CORS preflight, and if the OPTIONS
  // round-trip fails for any reason (CDN cache of old preflight, backend
  // not redeployed, etc.) the request fails and the loop still triggers
  // because our marker wasn't actually committed on the wire. A
  // query-string param needs no preflight and is impossible to lose.
  //
  // We also wrap interception in a per-tab `running` flag so even if the
  // marker is somehow stripped (extensions, weird shims) we still can't
  // re-enter while a flush is in flight.
  // Guards live on `window` (not in closure) so they're shared across every
  // copy of this script if Shopify happens to inject it more than once per
  // page (e.g. via theme.liquid AND a Section Rendering reload). The earlier
  // version used closure vars and got bypassed by a second script instance,
  // which is how we ended up with thousands of recursive link-email calls.
  if (!window.__winelyBridge) {
    window.__winelyBridge = {
      installed: false,
      everFiredForQid: {}, // qid → true once we've flushed for it this page
    };
  }
  var BRIDGE_MARKER = 'winely_bridge=1';

  (function installLinkEmailInterceptor() {
    if (typeof window.fetch !== 'function') return;
    if (window.__winelyBridge.installed) {
      log('fetch interceptor already installed (skipped duplicate)');
      return;
    }
    window.__winelyBridge.installed = true;

    var origFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string'
          ? input
          : (input && input.url) || '';
        var isLinkEmail = url.indexOf('/api/quiz/link-email') !== -1;
        var isSelf = url.indexOf(BRIDGE_MARKER) !== -1;

        if (isLinkEmail && !isSelf && init && init.body) {
          var bodyStr = typeof init.body === 'string' ? init.body : null;
          if (bodyStr) {
            var parsed = safeJSON(bodyStr);
            if (parsed && parsed.email && parsed.qid && !window.__winelyBridge.everFiredForQid[parsed.qid]) {
              // Mark BEFORE we schedule our own call — even if scheduling
              // somehow re-enters synchronously we'll already be guarded.
              window.__winelyBridge.everFiredForQid[parsed.qid] = true;
              log('intercepted POST /api/quiz/link-email (from quiz controller)', {
                qid: parsed.qid,
                email: parsed.email,
                hasEventsInPayload: !!(parsed.events && parsed.events.length),
              });
              try { localStorage.setItem('winely_email', parsed.email); } catch (e) {}
              if (parsed.first_name) {
                try { localStorage.setItem('winely_first_name', parsed.first_name); } catch (e) {}
              }
              setTimeout(function () {
                linkEmailToQid(parsed.qid, parsed.email, parsed.first_name || null);
              }, 100);
            }
          }
        }
      } catch (e) {
        log('fetch interceptor error', { err: String(e) });
      }
      return origFetch(input, init);
    };
    log('fetch interceptor installed');
  })();

  // ---------- Backend (primary transport) ----------
  // Pre-qid anonymous events (Quiz Started, Banner Clicked etc fired before
  // /api/quiz/submit returns a qid) get a per-tab session id as their
  // identifier. After link-email, /api/sync/klaviyo merges the session-id
  // profile into the email-keyed one along with the qid profile.
  function getSessionId() {
    try {
      var existing = sessionStorage.getItem('winely_session_id');
      if (existing) return existing;
    } catch (e) {}
    var fresh = cryptoRandomId();
    try { sessionStorage.setItem('winely_session_id', fresh); } catch (e) {}
    return fresh;
  }

  function postEventToBackend(metric, properties) {
    var customer = window.winelyCustomer || {};
    var email = customer.email || readEmailFromLS();
    var qid = localStorage.getItem('winely_qid') || null;
    var sessionId = (!email && !qid) ? getSessionId() : null;

    if (email && qid) {
      var firstName = customer.firstName || readFirstNameFromLS();
      log('postEventToBackend → linkEmailToQid (have both email+qid)', { metric: metric, qid: qid });
      linkEmailToQid(qid, email, firstName);
    } else {
      log('postEventToBackend (no link-email this call)', { metric: metric, hasEmail: !!email, qid: qid });
    }

    var payload = {
      metric: metric,
      email: email,
      qid: qid,
      sessionId: sessionId,
      properties: properties || {},
      uniqueId: cryptoRandomId(),
    };

    log('POST /api/klaviyo/event', { metric: metric, email: email, qid: qid, sessionId: sessionId });

    fetch(BACKEND_BASE + '/api/klaviyo/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    })
      .then(function (r) { log('POST /api/klaviyo/event response', { metric: metric, status: r.status }); })
      .catch(function (err) { log('POST /api/klaviyo/event FAILED', { metric: metric, err: String(err) }); });
  }

  function cryptoRandomId() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch (e) {}
    return 'winely-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  // ---------- Klaviyo SDK best-effort fallback (silent) ----------
  function kpush(method, payload) {
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push([method, payload]);
      } else if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push([method, payload]);
      } else {
        window.klaviyo = window.klaviyo || [];
        window.klaviyo.push([method, payload]);
      }
    } catch (e) {}
  }

  function ktrack(metric, properties) {
    if (!consentOK()) {
      log('ktrack skipped (no consent)', { metric: metric });
      return;
    }
    var customer = window.winelyCustomer || {};
    var email = customer.email || readEmailFromLS();
    log('ktrack', {
      metric: metric,
      hasEmail: !!email,
      emailSource: customer.email ? 'customer' : (email ? 'LS' : 'none'),
      properties: properties,
    });

    // Identified visitor → send immediately, bound to the email profile.
    if (email) {
      log('ktrack → send immediately (identified)', { metric: metric, email: email });
      postEventToBackend(metric, properties);
      kpush('track', [metric, properties || {}]);
      return;
    }

    // Anonymous visitor → buffer in localStorage instead of firing now.
    //
    // We can't safely fire klaviyo.track() onsite (creates a __kla_id-cookie
    // anonymous profile) or POST to /api/klaviyo/event with qid/sessionId
    // (creates an external_id-anonymous profile). Both later merge into the
    // email profile at subscribe time and make Klaviyo's welcome flow trigger
    // skip the profile as "returning visitor". Confirmed by Klaviyo support.
    //
    // Instead we hold the events here and ship them as part of
    // /api/quiz/link-email's payload, after which the backend tracks them
    // directly on the email profile in one shot.
    log('ktrack → buffer (anonymous)', { metric: metric });
    bufferEvent(metric, properties);
  }

  function bufferEvent(metric, properties) {
    try {
      var raw = localStorage.getItem(BUFFER_KEY);
      var list = raw ? safeJSON(raw) : null;
      if (!Array.isArray(list)) list = [];
      list.push({
        metric: metric,
        properties: properties || {},
        time: new Date().toISOString(),
        unique_id: cryptoRandomId(),
      });
      // Trim the oldest entries if a long browsing session has piled up.
      if (list.length > BUFFER_MAX) list = list.slice(list.length - BUFFER_MAX);
      localStorage.setItem(BUFFER_KEY, JSON.stringify(list));
      log('buffer write OK', { metric: metric, bufferSize: list.length });
    } catch (e) {
      log('buffer write FAILED', { metric: metric, err: String(e) });
    }
  }

  function readEventBuffer() {
    try {
      var raw = localStorage.getItem(BUFFER_KEY);
      if (!raw) return [];
      var list = safeJSON(raw);
      var arr = Array.isArray(list) ? list : [];
      log('buffer read', { size: arr.length, metrics: arr.map(function (e) { return e && e.metric; }) });
      return arr;
    } catch (e) {
      log('buffer read FAILED', { err: String(e) });
      return [];
    }
  }

  function clearEventBuffer() {
    try {
      localStorage.removeItem(BUFFER_KEY);
      log('buffer cleared');
    } catch (e) {}
  }

  function kidentify(traits) {
    if (!consentOK()) return;
    var email = traits && traits.$email;
    if (!email) return;
    try {
      if (sessionStorage.getItem(IDENTIFY_KEY) === email) return;
      sessionStorage.setItem(IDENTIFY_KEY, email);
    } catch (e) {}
    kpush('identify', traits);
  }

  function consentOK() {
    try {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.userCanBeTracked === 'function') {
        return api.userCanBeTracked() !== false;
      }
    } catch (e) {}
    return true;
  }

  // ---------- LS readers ----------
  function safeJSON(raw) { try { return JSON.parse(raw); } catch (e) { return null; } }

  function readQuizLS() {
    var key = LANDING_FLAG ? 'landing' : 'quiz';
    var raw = localStorage.getItem(key);
    if (!raw) return {};
    var parsed = safeJSON(raw) || {};
    return LANDING_FLAG ? (parsed.quiz || {}) : parsed;
  }

  function readEmailFromLS() {
    try {
      var direct = localStorage.getItem('winely_email');
      if (direct) return direct;
    } catch (e) {}
    var quiz = readQuizLS();
    var fromQuiz = (quiz && quiz.data && quiz.data.email) || null;
    if (fromQuiz) return fromQuiz;
    try {
      if (window.pnk && window.pnk.quizController && typeof window.pnk.quizController.restore === 'function') {
        var fromController = window.pnk.quizController.restore('data.email');
        if (fromController) return fromController;
      }
    } catch (e) {}
    return null;
  }

  function readFirstNameFromLS() {
    try {
      var v = localStorage.getItem('winely_first_name');
      if (v) return v;
    } catch (e) {}
    var quiz = readQuizLS();
    return (quiz && quiz.data && quiz.data.username) || null;
  }

  function readUsernameFromLS() {
    var quiz = readQuizLS();
    return (quiz && quiz.data && quiz.data.username) || null;
  }

  function quizSource() {
    var path = (window.location && window.location.pathname) || '';
    if (path.indexOf('/pages/varlamov') === 0) return 'varlamov';
    if (path.indexOf('/pages/quiz') === 0) return 'quiz';
    return 'other';
  }

  function readQuizPayload() {
    var quiz = readQuizLS();
    return {
      quizResult: quiz.result || null,
      quizId: localStorage.getItem('winely_qid') || null,
      userPreferences: quiz.preferences || null,
      categories: quiz.categories || null,
      username: (quiz.data && quiz.data.username) || null,
      source: quizSource(),
    };
  }

  // ---------- link-email helper (idempotent per session) ----------
  function alreadyLinked(qid) {
    try {
      var raw = sessionStorage.getItem(LINKED_KEY) || '';
      return raw.split(',').indexOf(qid) !== -1;
    } catch (e) { return false; }
  }

  function markLinked(qid) {
    try {
      var raw = sessionStorage.getItem(LINKED_KEY) || '';
      var list = raw ? raw.split(',') : [];
      if (list.indexOf(qid) === -1) list.push(qid);
      sessionStorage.setItem(LINKED_KEY, list.join(','));
    } catch (e) {}
  }

  function linkEmailToQid(qid, email, firstName) {
    if (!qid || !email) {
      log('linkEmailToQid skipped (no qid or email)', { qid: qid, hasEmail: !!email });
      return;
    }
    if (alreadyLinked(qid)) {
      log('linkEmailToQid skipped (alreadyLinked this session)', { qid: qid });
      return;
    }
    var sessionId = null;
    try { sessionId = sessionStorage.getItem('winely_session_id'); } catch (e) {}

    // Attach the anonymous event buffer so the backend can flush these events
    // onto the email profile right after subscribe. We send them in the same
    // call to avoid a window where subscribe has happened but the buffer is
    // still trapped in LS (tab close, navigation, etc).
    var bufferedEvents = readEventBuffer();

    log('linkEmailToQid REQUEST', {
      qid: qid,
      email: email,
      first_name: firstName,
      events_count: bufferedEvents.length,
      events_metrics: bufferedEvents.map(function (e) { return e && e.metric; }),
    });

    // Query-string marker tells our fetch interceptor to skip this
    // request. We use a query param (not a header) on purpose — see the
    // comment near the interceptor.
    fetch(BACKEND_BASE + '/api/quiz/link-email?' + BRIDGE_MARKER, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        qid: qid,
        email: email,
        first_name: firstName || undefined,
        session_id: sessionId || undefined,
        events: bufferedEvents,
      }),
      keepalive: true,
    })
      .then(function (r) {
        log('linkEmailToQid RESPONSE', { qid: qid, status: r.status, ok: r.ok });
        if (r.ok || r.status === 409) {
          markLinked(qid);
          // Buffer was handed off (persisted on the backend). Drop it locally
          // so a second link-email call (same qid, e.g. re-quiz) doesn't
          // double-send the same events.
          if (bufferedEvents.length) clearEventBuffer();
        }
      })
      .catch(function (err) {
        log('linkEmailToQid FAILED', { qid: qid, err: String(err) });
      });
  }

  // ---------- Identify on load ----------
  function maybeIdentifyOnLoad() {
    var customer = window.winelyCustomer || {};
    var email = customer.email || readEmailFromLS();
    if (!email) return;
    var firstName = customer.firstName || readUsernameFromLS();
    var traits = { $email: email };
    if (firstName) traits.$first_name = firstName;
    kidentify(traits);
  }

  // ---------- DOM event listeners ----------
  document.addEventListener('quiz:finished', function () {
    log('DOM event: quiz:finished received, polling for winely_qid…');
    var attempts = 0;
    var maxAttempts = 50; // 5s
    var iv = setInterval(function () {
      attempts++;
      var qid = localStorage.getItem('winely_qid');
      if (qid || attempts >= maxAttempts) {
        clearInterval(iv);
        log('quiz:finished resolved', { qid: qid, attempts: attempts });
        var payload = readQuizPayload();
        var customer = window.winelyCustomer || {};
        var email = customer.email || readEmailFromLS();
        if (email) {
          var traits = { $email: email };
          var firstName = customer.firstName || payload.username;
          if (firstName) traits.$first_name = firstName;
          kidentify(traits);
        }
        ktrack('Quiz Finished', payload);
      }
    }, 100);
  });

  document.addEventListener('tasteProfile:shown', function (e) {
    var products = Array.isArray(e.detail) ? e.detail : [];
    var matchings = products.map(function (p) { return p.matching; }).filter(function (m) { return typeof m === 'number'; });
    var brief = products.map(function (p) {
      return { id: p.id, title: p.title, handle: p.handle, matching: p.matching };
    });
    ktrack('Taste Profile Shown', {
      productsCount: products.length,
      matchingMin: matchings.length ? Math.min.apply(null, matchings) : null,
      matchingMax: matchings.length ? Math.max.apply(null, matchings) : null,
      products: brief,
    });
  });

  document.addEventListener('filter:tree', function (e) {
    var detail = e.detail || {};
    ktrack('Filter Tree Toggled', {
      type: detail.type || null,
      params: detail.params || null,
      lang: document.documentElement.lang || null,
    });
  });

  document.addEventListener('setAjaxProductDone', function (e) {
    var detail = e.detail || {};
    ktrack('Product Viewed', {
      productId: detail.id || null,
      title: detail.title || null,
      handle: detail.handle || null,
      lang: document.documentElement.lang || null,
    });
  });

  document.addEventListener('click', function (e) {
    var target = e.target;
    var lang = document.documentElement.lang || null;

    if (target.closest && target.closest('form.quiz-page-start-form button')) {
      ktrack('Quiz Started', { username: readUsernameFromLS(), lang: lang, source: quizSource() });
      return;
    }
    if (target.closest && target.closest('#qa-header-filter-button')) {
      ktrack('Filter Opened', { lang: lang });
      return;
    }
    var isFilterClose = target.classList && target.classList.contains('boost-pfs-filter-close');
    var isFilterCloseIcon = target.closest && target.closest('svg') && target.closest('header.filters-popup-header');
    if (isFilterClose || isFilterCloseIcon) {
      ktrack('Filter Closed', { lang: lang });
      return;
    }
    if (target.classList && target.classList.contains('boost-pfs-filter-clear-all')) {
      ktrack('Filter Cleared', { lang: lang });
      return;
    }
    var filterItem = target.closest && target.closest('.boost-pfs-filter-option-item');
    if (filterItem && !filterItem.classList.contains('choose-all')) {
      ktrack('Filter Toggled', { lang: lang });
      return;
    }
    var fullWidthBanner = target.closest && target.closest('section.shopify-section.heading-with-image-section');
    if (fullWidthBanner) {
      var anchor = fullWidthBanner.querySelector('a');
      var heading = fullWidthBanner.querySelector('a[role="button"] figcapture h2');
      ktrack('Banner Clicked', {
        bannerType: fullWidthBanner.dataset && fullWidthBanner.dataset.sectionType || null,
        bannerPath: anchor && anchor.href ? new URL(anchor.href).pathname : null,
        bannerHeading: heading ? heading.textContent.trim() : null,
        lang: lang,
      });
      return;
    }
    var halfWidthBanner = target.closest && target.closest('section.shopify-section.linked-images-section');
    if (halfWidthBanner) {
      ktrack('Banner Clicked', { bannerType: 'Half-width linked image banner', lang: lang });
    }
  });

  // ---------- Boot ----------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeIdentifyOnLoad);
  } else {
    maybeIdentifyOnLoad();
  }
})();
